import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import WCButton from "@/components/WCButton";
import AnimateIn from "@/components/AnimateIn";
import { getProductBySlug } from "@/data/mockData";
import { useShop } from "@/context/ShopContext";
import { Trash2 } from "lucide-react";

const TABS = [
  { id: "profile", label: "PROFILE" },
  { id: "orders", label: "ORDERS" },
  { id: "wishlist", label: "WISHLIST" },
  { id: "waitlist", label: "WAITLIST" },
];

export default function Account() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");

  useEffect(() => {
    if (!loading && !user && !window.location.hash?.includes("session_id=")) {
      navigate("/login");
    }
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center text-white/50 wc-label text-sm">
        LOADING...
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen pt-[160px] md:pt-[200px] pb-24 px-6" data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        <AnimateIn variant="fade">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="wc-label text-xs text-white/50">ACCOUNT</div>
              <h1 className="wc-heading text-white text-4xl md:text-6xl tracking-widest mt-2">
                {user.name ? user.name.toUpperCase() : "MEMBER"}
              </h1>
              <div className="text-white/60 text-sm mt-2">{user.email}</div>
            </div>
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="wc-btn"
              data-testid="account-logout"
            >
              Log Out
            </button>
          </div>
        </AnimateIn>

        <div className="flex flex-wrap gap-6 md:gap-10 mt-14 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`wc-label text-xs md:text-sm pb-3 border-b-2 transition-colors ${
                tab === t.id ? "text-white border-white" : "text-white/40 border-transparent"
              }`}
              data-testid={`account-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-12">
          {tab === "profile" && <ProfileTab user={user} updateProfile={updateProfile} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "wishlist" && <WishlistTab />}
          {tab === "waitlist" && <WaitlistTab email={user.email} />}
        </div>
      </div>
    </div>
  );
}

const ProfileTab = ({ user, updateProfile }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    address_line1: user.address_line1 || "",
    address_line2: user.address_line2 || "",
    city: user.city || "",
    state: user.state || "",
    postal_code: user.postal_code || "",
    country: user.country || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await updateProfile(form);
    setSaving(false);
    if (res.ok) toast.success("Profile saved.");
    else toast.error(res.error);
  };

  return (
    <form onSubmit={save} className="grid md:grid-cols-2 gap-5 max-w-2xl" data-testid="profile-form">
      <Field label="NAME" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
      <Field label="COUNTRY" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="profile-country" />
      <div className="md:col-span-2">
        <Field
          label="ADDRESS LINE 1"
          value={form.address_line1}
          onChange={(v) => setForm({ ...form, address_line1: v })}
          testid="profile-address1"
        />
      </div>
      <div className="md:col-span-2">
        <Field
          label="ADDRESS LINE 2"
          value={form.address_line2}
          onChange={(v) => setForm({ ...form, address_line2: v })}
          testid="profile-address2"
        />
      </div>
      <Field label="CITY" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="profile-city" />
      <Field label="STATE" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="profile-state" />
      <Field
        label="POSTAL CODE"
        value={form.postal_code}
        onChange={(v) => setForm({ ...form, postal_code: v })}
        testid="profile-postal"
      />
      <div className="md:col-span-2 mt-4">
        <WCButton type="submit" filled disabled={saving} data-testid="profile-save">
          {saving ? "SAVING..." : "SAVE"}
        </WCButton>
      </div>
    </form>
  );
};

const Field = ({ label, onChange, testid, value, ...rest }) => (
  <div>
    <label className="wc-label text-xs text-white block mb-2">{label}</label>
    <input
      {...rest}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-transparent border border-white/40 focus:border-white text-white outline-none px-4 py-3 text-base transition-colors"
    />
  </div>
);

const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  if (orders === null) return <p className="text-white/40 wc-label text-xs">LOADING...</p>;
  return (
    <div data-testid="orders-tab">
      <p className="text-white/60 max-w-xl">
        Your orders from the Shopify store will appear here once the integration is connected.
      </p>
      {orders.length === 0 ? (
        <div className="mt-10 border border-white/10 p-10 text-center text-white/40 wc-label text-xs">
          NO ORDERS YET.
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="border border-white/10 p-5 text-white/80">
              Order #{o.id} — ${o.total}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WishlistTab = () => {
  const [items, setItems] = useState(null);
  const { getByHandle } = useShop();
  const load = async () => {
    try {
      const { data } = await api.get("/wishlist");
      setItems(data);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const remove = async (slug) => {
    await api.delete(`/wishlist/${slug}`);
    await load();
  };

  if (items === null) return <p className="text-white/40 wc-label text-xs">LOADING...</p>;
  if (items.length === 0)
    return (
      <div className="border border-white/10 p-10 text-center" data-testid="wishlist-empty">
        <div className="wc-label text-xs text-white/40">NOTHING SAVED YET</div>
        <Link to="/shop" className="wc-label text-xs text-white underline mt-4 inline-block">
          Explore the collection
        </Link>
      </div>
    );

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="wishlist-grid">
      {items.map((w) => {
        const p = getByHandle(w.product_slug) || getProductBySlug(w.product_slug);
        if (!p) return null;
        const img = p.images?.[0];
        return (
          <div key={w.product_slug} className="relative" data-testid={`wishlist-item-${w.product_slug}`}>
            <Link to={`/shop/${p.handle || p.slug}`} className="block">
              <div className="aspect-square bg-[#0a0a0a] overflow-hidden">
                {img && <img src={img} alt={p.name} className="wc-image w-full h-full object-cover" />}
              </div>
              <div className="wc-heading text-white text-xl tracking-widest mt-3">{p.name}</div>
              <div className="text-sm text-white/60">${p.price}</div>
            </Link>
            <button
              onClick={() => remove(w.product_slug)}
              className="absolute top-3 right-3 bg-black/70 border border-white/40 p-2 text-white hover:bg-white hover:text-black transition-colors"
              data-testid={`wishlist-remove-${w.product_slug}`}
              aria-label="Remove from wishlist"
            >
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const WaitlistTab = ({ email }) => {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    api
      .get(`/waitlist/by-email/${encodeURIComponent(email)}`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]));
  }, [email]);

  if (entries === null) return <p className="text-white/40 wc-label text-xs">LOADING...</p>;
  if (entries.length === 0)
    return (
      <div className="border border-white/10 p-10 text-center" data-testid="waitlist-empty">
        <div className="wc-label text-xs text-white/40">NOT ON ANY WAITLIST YET</div>
        <Link to="/drops" className="wc-label text-xs text-white underline mt-4 inline-block">
          View active drops
        </Link>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="waitlist-list">
      {entries.map((e) => (
        <div key={e.id} className="border border-white/10 p-5">
          <div className="wc-label text-xs text-white/50">SOURCE</div>
          <div className="wc-heading text-white text-2xl tracking-widest mt-1">
            {e.source?.toUpperCase() || "HOMEPAGE"}
          </div>
          <div className="flex gap-6 mt-3 wc-label text-xs text-white/70">
            {e.international && <span>INTERNATIONAL</span>}
            {e.domestic && <span>DOMESTIC</span>}
          </div>
          <div className="text-xs text-white/40 mt-3">
            Joined {new Date(e.created_at).toLocaleDateString()}
          </div>
        </div>
      ))}
    </div>
  );
};
